import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff } from "lucide-react";
import logoLoginCadastro from "@/assets/logo-login-cadastro.png";
import { Country, COUNTRIES, AVAILABLE_COUNTRIES } from "@/types/internationalization";
import { validateCpfCnpj } from "@/utils/validators";
import { enviarEmailBemvindoOperador } from "@/services/emailOperador";
import "@/styles/auth-animations.css";

export default function Cadastro() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState<string>("");
  
  // Detectar código de referência na URL
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setReferralCode(ref);
      setFormData(prev => ({ ...prev, referralCode: ref }));
      toast.info(`Código de indicação detectado! Você ganhará 7 dias grátis! 🎁`);
    }
  }, [searchParams]);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    empresa: "",
    nome: "",
    telefone: "",
    cpfCnpj: "",
    country: "BR" as Country,
    referralCode: "",
    tipoNegocio: "" as "atelie" | "confeccao" | "grafica" | "outro" | "",
    conheceuPor: "" as "amigos" | "google" | "instagram" | "indicacao" | "facebook" | "linkedin" | "youtube" | "outro" | "",
  });
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [cpfCnpjError, setCpfCnpjError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePasswordStrength = (password: string): { error: string; strength: number } => {
    const minLength = 6;
    if (!password) {
      return { error: "", strength: 0 };
    }
    if (password.length < minLength) {
      return { 
        error: `🔒 Senha fraca! Mínimo ${minLength} caracteres (você tem ${password.length})`, 
        strength: Math.floor((password.length / minLength) * 100) 
      };
    }
    
    // Calcular força da senha
    let strength = 60; // Base por atingir mínimo
    if (/[A-Z]/.test(password)) strength += 10; // Maiúscula
    if (/[0-9]/.test(password)) strength += 10; // Números
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength += 20; // Caracteres especiais
    
    return { error: "", strength: Math.min(strength, 100) };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validação em tempo real do CPF/CNPJ
    if (name === 'cpfCnpj') {
      const validation = validateCpfCnpj(value);
      setCpfCnpjError(validation.isValid ? "" : validation.errors[0]);
    }

    // Validação em tempo real da senha
    if (name === 'password') {
      const { error, strength } = validatePasswordStrength(value);
      setPasswordError(error);
      setPasswordStrength(strength);
    }
  };

  const handleCountryChange = (value: Country) => {
    setFormData(prev => ({
      ...prev,
      country: value
    }));
  };

  const handleCadastro = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação do CPF/CNPJ
    const cpfCnpjValidation = validateCpfCnpj(formData.cpfCnpj);
    if (!cpfCnpjValidation.isValid) {
      toast.error(cpfCnpjValidation.errors[0]);
      setCpfCnpjError(cpfCnpjValidation.errors[0]);
      return;
    }

    // Validação da senha
    if (formData.password.length < 6) {
      toast.error(`🔒 Senha insuficiente! Mínimo 6 caracteres (você tem ${formData.password.length})`);
      setPasswordError(`🔒 Senha fraca! Mínimo 6 caracteres (você tem ${formData.password.length})`);
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("❌ As senhas não coincidem. Por favor, verifique e tente novamente.");
      return;
    }

    // Validação dos campos de onboarding
    if (!formData.tipoNegocio) {
      toast.error("❌ Selecione seu tipo de negócio para continuar");
      return;
    }

    if (!formData.conheceuPor) {
      toast.error("❌ Conte-nos como conheceu o Ateliê Pro para continuar");
      return;
    }

    if (!acceptedTerms) {
      toast.error("Você deve aceitar os Termos de Uso para continuar");
      return;
    }

    if (!acceptedPrivacy) {
      toast.error("Você deve aceitar a Política de Privacidade para continuar");
      return;
    }

    setLoading(true);

    try {
      // Criar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.nome,
          },
        },
      })

      if (authError) {
        toast.error(authError.message || 'Erro ao criar usuário')
        return
      }

      if (!authData.user?.id) {
        toast.error('Erro ao criar usuário')
        return
      }

      const userId = authData.user.id

      // Aguardar propagação
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Criar empresa + relacionamento (com permissões administrativas)
      const trialEndDate = new Date()
      trialEndDate.setDate(trialEndDate.getDate() + 7)

      const { data: empresaResult, error: empresaError } = await supabase
        .rpc('create_empresa_with_owner', {
          p_nome: formData.empresa,
          p_email: formData.email,
          p_telefone: formData.telefone,
          p_responsavel: formData.nome,
          p_cpf_cnpj: formData.cpfCnpj.replace(/\D/g, ''),
          p_country: formData.country,
          p_trial_end_date: trialEndDate.toISOString(),
          p_accepted_terms: true,
          p_accepted_privacy: true,
          p_accepted_terms_at: new Date().toISOString(),
          p_accepted_privacy_at: new Date().toISOString(),
          p_terms_version: '2026-02-18-v2',
          p_privacy_version: '2026-02-18-v2',
          p_tipo_negocio: formData.tipoNegocio,
          p_conheceu_por: formData.conheceuPor,
        })

      if (empresaError) {
        console.error('Empresa error:', empresaError)
        toast.error('Erro ao criar empresa: ' + (empresaError.message || 'Desconhecido'))
        return
      }

      if (!empresaResult || empresaResult.length === 0) {
        toast.error('Erro ao criar empresa: Resposta vazia')
        return
      }

      const empresaId = empresaResult[0]?.empresa_id

      toast.success("🎉 Bem-vindo ao Ateliê Pro! Redirecionando para o dashboard...");

      // Disparar email de boas-vindas via Edge Function (assíncrono)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const internalToken = import.meta.env.VITE_INTERNAL_AUTOMATION_TOKEN

      if (supabaseUrl && internalToken && empresaId) {
        fetch(`${supabaseUrl}/functions/v1/send-welcome-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-internal-token': internalToken
          },
          body: JSON.stringify({ empresa_id: empresaId })
        }).catch(err => console.error('Erro ao disparar email:', err))
      } else {
        console.warn('Email não disparado: variáveis de ambiente não configuradas', {
          hasUrl: !!supabaseUrl,
          hasToken: !!internalToken,
          hasEmpresa: !!empresaId
        })
      }

      // Enviar email de operador (assíncrono, não bloqueia)
      enviarEmailBemvindoOperador({
        to: formData.email,
        fullName: formData.nome,
        empresaNome: formData.empresa,
        linkCadastro: `${window.location.origin}/login`
      }).catch(err => console.error('Erro ao enviar email operador:', err));

      // Fazer login automático
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      })

      if (signInError) {
        console.error("Erro ao fazer login:", signInError)
        navigate("/login")
        return
      }

      navigate("/")
    } catch (error: unknown) {
      console.error("Erro no cadastro:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro ao fazer cadastro";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-background min-h-screen flex items-center justify-center p-4">
      <div className="auth-container w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logoLoginCadastro} alt="Ateliê Pro" className="auth-logo h-20 mx-auto mb-4" />
          <p className="auth-subtitle text-purple-100">Gestão profissional para ateliês</p>
        </div>

        <Card className="auth-card bg-white shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-purple-900">Cadastro</CardTitle>
            <p className="text-purple-600">Crie sua conta e empresa</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCadastro} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="empresa" className="text-purple-900 font-medium">
                  Nome da Empresa <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="empresa"
                  name="empresa"
                  value={formData.empresa}
                  onChange={handleChange}
                  className="auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  placeholder="Ex: Ateliê da Maria"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome" className="text-purple-900 font-medium">
                  Seu Nome <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  className="auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  placeholder="Maria Silva"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-purple-900 font-medium">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  placeholder="seu@email.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpfCnpj" className="text-purple-900 font-medium">
                  CPF ou CNPJ <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="cpfCnpj"
                  name="cpfCnpj"
                  value={formData.cpfCnpj}
                  onChange={handleChange}
                  className={`auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 ${
                    cpfCnpjError ? 'border-red-400' : ''
                  }`}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  required
                />
                {cpfCnpjError && (
                  <p className="text-red-500 text-sm">{cpfCnpjError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone" className="text-purple-900 font-medium">
                  Telefone <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  className="auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  placeholder="(11) 99999-9999"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country" className="text-purple-900 font-medium">
                  País <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.country} onValueChange={handleCountryChange}>
                  <SelectTrigger className="auth-input bg-white border-gray-300 text-gray-900">
                    <SelectValue placeholder="Selecione seu país" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_COUNTRIES.map((country) => {
                      const config = COUNTRIES[country];
                      return (
                        <SelectItem key={country} value={country}>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{config.flag}</span>
                            <span>{config.name}</span>
                            <span className="text-muted-foreground text-sm">
                              ({config.currencySymbol})
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Tipo de Negócio */}
              <div className="space-y-2">
                <Label htmlFor="tipoNegocio" className="text-purple-900 font-medium">
                  Qual é o seu tipo de negócio? <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.tipoNegocio} onValueChange={(value) => setFormData(prev => ({ ...prev, tipoNegocio: value as any }))}>
                  <SelectTrigger className="auth-input bg-white border-gray-300 text-gray-900">
                    <SelectValue placeholder="Selecione seu tipo de negócio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="atelie">👗 Ateliê</SelectItem>
                    <SelectItem value="confeccao">🏭 Confecção</SelectItem>
                    <SelectItem value="grafica">🖨️ Gráfica</SelectItem>
                    <SelectItem value="outro">📌 Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Como conheceu o Ateliê Pro */}
              <div className="space-y-2">
                <Label htmlFor="conheceuPor" className="text-purple-900 font-medium">
                  Como você conheceu o Ateliê Pro? <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.conheceuPor} onValueChange={(value) => setFormData(prev => ({ ...prev, conheceuPor: value as any }))}>
                  <SelectTrigger className="auth-input bg-white border-gray-300 text-gray-900">
                    <SelectValue placeholder="Selecione uma opção" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amigos">👥 Indicação de Amigos</SelectItem>
                    <SelectItem value="google">🔍 Busca na Internet</SelectItem>
                    <SelectItem value="instagram">📸 Instagram</SelectItem>
                    <SelectItem value="facebook">📱 Facebook</SelectItem>
                    <SelectItem value="youtube">🎬 YouTube</SelectItem>
                    <SelectItem value="linkedin">💼 LinkedIn</SelectItem>
                    <SelectItem value="indicacao">🎁 Código de Indicação</SelectItem>
                    <SelectItem value="outro">📌 Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-purple-900 font-medium">
                  Senha <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    className={`auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 pr-10 ${
                      passwordError ? 'border-red-400' : ''
                    }`}
                    placeholder="Mínimo 6 caracteres (ex: abc123)"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {formData.password && (
                  <div className="space-y-2">
                    {/* Barra de força da senha */}
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          passwordStrength < 50 ? 'bg-red-500' :
                          passwordStrength < 75 ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${passwordStrength}%` }}
                      />
                    </div>
                    {/* Indicador de força */}
                    <p className={`text-xs font-medium ${
                      passwordError ? 'text-red-500' :
                      passwordStrength < 50 ? 'text-red-500' :
                      passwordStrength < 75 ? 'text-yellow-600' :
                      'text-green-600'
                    }`}>
                      {passwordError ? (
                        passwordError
                      ) : passwordStrength < 50 ? (
                        `🔓 Fraca (${passwordStrength}%) - Adicione números ou caracteres especiais`
                      ) : passwordStrength < 75 ? (
                        `🔐 Média (${passwordStrength}%) - Boa escolha!`
                      ) : (
                        `🔒 Forte (${passwordStrength}%) - Excelente segurança!`
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-purple-900 font-medium">
                  Confirmar Senha <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Campo de Código de Referência */}
              <div className="space-y-2">
                <Label htmlFor="referralCode" className="text-purple-900 font-medium">
                  Código de Indicação <span className="text-gray-500 text-xs">(Opcional)</span>
                </Label>
                <Input
                  id="referralCode"
                  name="referralCode"
                  type="text"
                  value={formData.referralCode}
                  onChange={handleChange}
                  className="auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 uppercase"
                  placeholder="Digite o código (ex: A123456)"
                  maxLength={20}
                />
                {formData.referralCode && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <span className="text-green-600">🎁</span>
                    Você ganhará 7 dias grátis adicionais!
                  </p>
                )}
                {referralCode && !formData.referralCode && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <span>✅</span>
                    Código detectado na URL! Você ganhará 7 dias grátis!
                  </p>
                )}
              </div>

              {/* Checkboxes de Termos e Privacidade */}
              <div className="space-y-3 pt-4 border-t border-gray-200">
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="acceptedTerms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                    required
                  />
                  <label htmlFor="acceptedTerms" className="text-sm text-gray-700 leading-relaxed">
                    Li e aceito os{" "}
                    <Link
                      to="/termos"
                      target="_blank"
                      className="text-purple-600 hover:text-purple-700 underline font-medium"
                    >
                      Termos de Uso
                    </Link>{" "}
                    <span className="text-red-500">*</span>
                  </label>
                </div>

                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="acceptedPrivacy"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    className="mt-1 h-4 w-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                    required
                  />
                  <label htmlFor="acceptedPrivacy" className="text-sm text-gray-700 leading-relaxed">
                    Li e aceito a{" "}
                    <Link
                      to="/privacidade"
                      target="_blank"
                      className="text-purple-600 hover:text-purple-700 underline font-medium"
                    >
                      Política de Privacidade
                    </Link>{" "}
                    <span className="text-red-500">*</span>
                  </label>
                </div>
              </div>
              
              <Button
                type="submit"
                className="auth-button w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 hover:from-purple-700 hover:via-purple-600 hover:to-pink-600 text-white font-semibold py-3"
                disabled={loading}
              >
                {loading ? "Criando conta..." : "Criar Conta"}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Já tem uma conta?{" "}
                <Link to="/login" className="auth-link text-purple-600 font-medium">
                  Faça login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
